/* Premium dashboard with animated gauges, radar chart, top KPIs.

Visually impactful side-panel for the Cartographie module.
*/
import { useEffect, useState } from 'react';
import { TrendingUp, TrendingDown, Activity, Target, Users, Pill, Stethoscope, Building2 } from 'lucide-react';

function CircularGauge({ value, max = 100, label, color = '#1B2A4A', size = 110, sublabel = '' }) {
  const [animated, setAnimated] = useState(0);
  useEffect(() => {
    const start = Date.now();
    const dur = 800;
    const tick = () => {
      const t = Math.min(1, (Date.now() - start) / dur);
      const eased = 1 - Math.pow(1 - t, 3);
      setAnimated(value * eased);
      if (t < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [value]);
  const pct = Math.max(0, Math.min(1, animated / max));
  const circumference = 2 * Math.PI * 42;
  const offset = circumference * (1 - pct);
  return (
    <div className="flex flex-col items-center" data-testid={`gauge-${label}`}>
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} viewBox="0 0 100 100" style={{ transform: 'rotate(-90deg)' }}>
          <circle cx="50" cy="50" r="42" fill="none" stroke="#E5E7EB" strokeWidth="8" />
          <circle
            cx="50" cy="50" r="42" fill="none"
            stroke={color} strokeWidth="8" strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            style={{ transition: 'stroke-dashoffset 100ms linear', filter: `drop-shadow(0 0 6px ${color}44)` }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-2xl font-black" style={{ color }}>{Math.round(animated)}</span>
          {sublabel && <span className="text-[9px] text-gray-400 font-bold uppercase">{sublabel}</span>}
        </div>
      </div>
      <p className="text-[10px] text-gray-500 font-bold uppercase mt-1 text-center">{label}</p>
    </div>
  );
}

function RadarChart({ data, size = 220 }) {
  // data: [{ axis, value (0-100), national }]
  const center = size / 2;
  const radius = size * 0.4;
  const N = data.length;
  const points = data.map((d, i) => {
    const angle = -Math.PI / 2 + (i / N) * 2 * Math.PI;
    return {
      ...d,
      x: center + radius * Math.cos(angle) * (d.value / 100),
      y: center + radius * Math.sin(angle) * (d.value / 100),
      lx: center + (radius + 14) * Math.cos(angle),
      ly: center + (radius + 14) * Math.sin(angle),
      nx: center + radius * Math.cos(angle) * (d.national / 100),
      ny: center + radius * Math.sin(angle) * (d.national / 100),
    };
  });
  const polygon = points.map(p => `${p.x},${p.y}`).join(' ');
  const polygonNat = points.map(p => `${p.nx},${p.ny}`).join(' ');

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} data-testid="radar-chart">
      {/* Grid circles */}
      {[0.25, 0.5, 0.75, 1].map(r => (
        <circle key={r} cx={center} cy={center} r={radius * r} fill="none" stroke="#E5E7EB" strokeWidth="1" />
      ))}
      {/* Axes */}
      {points.map((p, i) => {
        const angle = -Math.PI / 2 + (i / N) * 2 * Math.PI;
        const ex = center + radius * Math.cos(angle);
        const ey = center + radius * Math.sin(angle);
        return <line key={i} x1={center} y1={center} x2={ex} y2={ey} stroke="#E5E7EB" strokeWidth="1" />;
      })}
      {/* National polygon */}
      <polygon points={polygonNat} fill="#94A3B833" stroke="#94A3B8" strokeWidth="1.5" strokeDasharray="3,3" />
      {/* Your polygon */}
      <polygon points={polygon} fill="#B8860B33" stroke="#B8860B" strokeWidth="2" />
      {/* Vertices */}
      {points.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r="4" fill="#B8860B" stroke="white" strokeWidth="2" />
      ))}
      {/* Labels */}
      {points.map((p, i) => (
        <text key={i} x={p.lx} y={p.ly} textAnchor="middle" dominantBaseline="middle"
              fontSize="9" fontWeight="700" fill="#1B2A4A">
          {p.axis}
        </text>
      ))}
    </svg>
  );
}

function MiniStatCard({ icon: Icon, label, value, delta, color = '#1B2A4A', testid }) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-3 hover:shadow-md transition" data-testid={testid}>
      <div className="flex items-center gap-2 mb-1">
        <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${color}15` }}>
          <Icon size={13} style={{ color }} />
        </div>
        <span className="text-[10px] uppercase font-bold text-gray-400">{label}</span>
      </div>
      <p className="text-2xl font-black" style={{ color }}>{value}</p>
      {delta != null && (
        <div className="flex items-center gap-1 mt-0.5">
          {delta > 0 ? <TrendingUp size={10} className="text-green-500" /> : <TrendingDown size={10} className="text-red-500" />}
          <span className={`text-[10px] font-bold ${delta > 0 ? 'text-green-500' : 'text-red-500'}`}>{delta > 0 ? '+' : ''}{delta}%</span>
        </div>
      )}
    </div>
  );
}

export default function PremiumDashboard({ summary, concurrence, prescripteurs, demographics, opportunities }) {
  const counts = summary?.by_type || {};

  // Compute scores 0-100 for the 4 main gauges
  const concurrenceScore = (() => {
    if (!concurrence) return 50;
    const lvl = concurrence.density?.level;
    return { saturated: 95, high: 75, medium: 50, low: 25, very_low: 10 }[lvl] || 50;
  })();
  const medicalScore = (() => {
    if (!prescripteurs) return 50;
    return Math.min(100, Math.max(0, prescripteurs.pct_vs_nat));
  })();
  const opportunitiesScore = Math.min(100, (opportunities?.length || 0) * 20);
  const globalScore = Math.round((medicalScore * 0.3 + (100 - concurrenceScore) * 0.3 + opportunitiesScore * 0.4));

  // Radar data
  const radarData = [
    { axis: 'Concurrence', value: 100 - concurrenceScore, national: 50 },
    { axis: 'M\u00e9dical', value: Math.min(100, medicalScore), national: 100 },
    { axis: 'Seniors', value: Math.min(100, (demographics?.pct_65_plus || 21) * 4), national: 80 },
    { axis: 'Opp.', value: opportunitiesScore, national: 50 },
    { axis: 'Trafic', value: Math.min(100, (counts.bus_stop || 0) * 3 + (counts.gare || 0) * 20), national: 50 },
    { axis: 'Commerce', value: Math.min(100, (counts.supermarche || 0) * 8 + (counts.banque || 0) * 4 + (counts.centre_commercial || 0) * 15), national: 50 },
  ];

  return (
    <div className="space-y-3" data-testid="premium-dashboard">
      {/* Score global */}
      <div className="bg-gradient-to-br from-[#1B2A4A] to-[#2C3E5C] rounded-2xl p-4 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-32 h-32 bg-[#B8860B]/20 rounded-full -translate-y-12 translate-x-12 blur-2xl" />
        <div className="relative">
          <p className="text-[10px] uppercase tracking-wider font-bold text-white/60">Score d'attractivit&eacute; territoriale</p>
          <div className="flex items-end gap-3 mt-1">
            <span className="text-5xl font-black text-[#B8860B] leading-none" data-testid="global-score">{globalScore}</span>
            <span className="text-sm text-white/60 mb-2">/ 100</span>
          </div>
          <div className="mt-2 flex gap-1">
            {[...Array(10)].map((_, i) => (
              <div key={i} className="h-1.5 flex-1 rounded-full" style={{ background: i < globalScore / 10 ? '#B8860B' : 'rgba(255,255,255,0.15)' }} />
            ))}
          </div>
        </div>
      </div>

      {/* Quad gauges */}
      <div className="bg-white rounded-2xl border border-gray-100 p-3">
        <p className="text-[10px] uppercase tracking-wider font-bold text-gray-400 mb-2 px-1">Indicateurs cl&eacute;s</p>
        <div className="grid grid-cols-4 gap-1">
          <CircularGauge value={concurrenceScore} label="Concurrence" color="#DC2626" size={86} sublabel="/100" />
          <CircularGauge value={Math.min(100, medicalScore)} label="M&eacute;dical" color="#10B981" size={86} sublabel="/100" />
          <CircularGauge value={opportunitiesScore} label="Opportunit&eacute;s" color="#B8860B" size={86} sublabel="/100" />
          <CircularGauge value={Math.min(100, (counts.ecole || 0) * 5)} label="Jeunes" color="#3B82F6" size={86} sublabel="/100" />
        </div>
      </div>

      {/* Radar chart */}
      <div className="bg-white rounded-2xl border border-gray-100 p-3">
        <div className="flex items-center justify-between mb-2 px-1">
          <p className="text-[10px] uppercase tracking-wider font-bold text-gray-400">Comparatif vs France</p>
          <div className="flex items-center gap-2 text-[9px]">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#B8860B]" />Vous</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-gray-400" />France</span>
          </div>
        </div>
        <div className="flex justify-center">
          <RadarChart data={radarData} size={220} />
        </div>
      </div>

      {/* Mini stats grid */}
      <div className="grid grid-cols-2 gap-2">
        <MiniStatCard testid="mini-pharmacies" icon={Pill} label="Pharmacies" value={counts.pharmacie || 0} color="#DC2626" />
        <MiniStatCard testid="mini-medecins" icon={Stethoscope} label="M&eacute;decins" value={(counts.medecin_gen || 0) + (counts.medecin_spe || 0)} color="#10B981" />
        <MiniStatCard testid="mini-ehpad" icon={Building2} label="EHPAD" value={counts.ehpad || 0} color="#7C3AED" />
        <MiniStatCard testid="mini-ecoles" icon={Users} label="&Eacute;coles" value={(counts.ecole || 0) + (counts.creche || 0) + (counts.college || 0)} color="#3B82F6" />
        <MiniStatCard testid="mini-supermarches" icon={Activity} label="Supermarch&eacute;s" value={counts.supermarche || 0} color="#84CC16" />
        <MiniStatCard testid="mini-trafic" icon={Target} label="Arr&ecirc;ts bus" value={counts.bus_stop || 0} color="#A78BFA" />
      </div>
    </div>
  );
}
